import { memo } from 'react';

import size from '@/assets/theme/size';
import DropdownContainer from '@/components/atomic/atoms/utils/portals/dropdown-container';
import DesktopMenuContainer from '@/components/atomic/molecules/utils/portals/desktop-menu-container';
import FrostedGlassBar from '@/components/atomic/molecules/utils/portals/desktop-menu-container/frosted-glass-bar';
import MobileMenuContainer from '@/components/atomic/molecules/utils/portals/mobile-menu-container';
import ViewportRender from '@/components/atomic/molecules/utils/viewport/viewport-render';
import withExposer from '@/hocs/with-exposer';
import useDebounceStore from '@/hooks/use-debounce-store';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import useStore from '@/state';
import getAnimationDirection from '@/state/selectors/navigation/get-animation-direction.ts';
import getAnimationDuration from '@/state/selectors/navigation/get-animation-duration';
import getDropdownId from '@/state/selectors/navigation/get-dropdown-id';
import getDropdownSegments from '@/state/selectors/navigation/get-dropdown-segments';
import getRefByKey from '@/state/selectors/ref/get-ref-by-key';
import { IStore } from '@/state/slices/typings';
import { BasicComponentProps } from '@/typings/generic';

const { laptop } = size;

const DropdownHandlerComponent: React.FC<BasicComponentProps> = ({
	$exposerSuffix,
}) => {
	const dropdownId = useStore(getDropdownId);
	const animationDuration = useStore(getAnimationDuration);
	const animationDirection = useStore(getAnimationDirection);
	const [, debouncedDropdownSegments] = useDebounceStore(
		getDropdownSegments,
		animationDuration,
	);

	const frostedGlassBarHeight = document.querySelector(
		'#naas-dropdown-vehicles',
	)
		? 56
		: undefined;

	const headerRef = useStore((state: IStore) => getRefByKey(state, 'header'));
	const entries = useResizeObserver(headerRef?.ref.current);
	const $paddingTop =
		animationDirection === 'right'
			? 0
			: (entries?.[0]?.contentRect.bottom ?? 0);

	const dropdownStyle = (dropdownId?: string) => {
		const usesSidebarPanel = [
			'my-account',
			'contact-and-help',
			'preferred-retailer',
		];

		if (dropdownId && usesSidebarPanel.includes(dropdownId)) {
			return 'sidebar-panel';
		}
		return '';
	};

	if (!debouncedDropdownSegments || entries.length === 0) return null;

	const hasBreadcrumbs = !!document.querySelector(
		'.dxnav__breadcrumbs-container',
	);

	return (
		<DropdownContainer
			$paddingTop={$paddingTop}
			$exposerSuffix={$exposerSuffix}
			$frostedGlassBarHeight={frostedGlassBarHeight}
			id={`naas-dropdown-${dropdownId}`}
			className={dropdownStyle(dropdownId)}
			hasBreadcrumbs={hasBreadcrumbs}
		>
			<ViewportRender
				Component={MobileMenuContainer}
				componentProps={{
					$paddingTop,
				}}
				$breakpoints={{
					[laptop]: {
						Component: DesktopMenuContainer,
						componentProps: {
							$frostedGlassBarHeight: frostedGlassBarHeight,
						},
					},
				}}
			/>
			{frostedGlassBarHeight ? (
				<ViewportRender
					$breakpoints={{
						[laptop]: {
							Component: FrostedGlassBar,
							componentProps: { $height: frostedGlassBarHeight },
						},
					}}
				/>
			) : null}
		</DropdownContainer>
	);
};

const DropdownHandler = memo(
	withExposer(DropdownHandlerComponent, 'dropdownHandler'),
);

export default DropdownHandler;
